// Cloud Explorer page
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '@config/constants';
import { Upload as UploadIcon, FolderPlus, Folder as FolderIcon, ChevronRight } from 'lucide-react';
import { FileList } from './components/FileList';
import { FileFilters } from './components/FileFilters';
import { FileUploader } from '@features/upload/components/FileUploader';
import { AudioPreview } from '@components/audio/AudioPreview';
import { FolderActions } from '@components/folder/FolderActions';
import { CreateFolderModal } from '@components/folder/CreateFolderModal';
import { BatchActions } from '@components/file/BatchActions';
import { QuickEditModal } from './QuickEditModal';
import { useFileStore } from '@stores/useFileStore';
import { useCloudExplorerStore } from '@stores/useCloudExplorerStore';
import { useDragAndDrop } from '@hooks';
import { useToast } from '@components/ui';
import { AudioFile, Folder, FileStatus, UserPlan } from '@types';
import { cn } from '@utils/classNames';
import { SkeletonTable } from '@components/ui';
import { FileListSkeleton } from '@components/ui/SkeletonLoader';
import { EmptyFileList, EmptySearchResults, EmptyCloudExplorer } from '@components/ui/EmptyState';
import { groupFilesByName, linkStemsFoldersToTracks, sortFileGroupsByCreatedAtDesc } from '@utils/fileGrouping';
import { UpgradeModal } from '@components/modals/UpgradeModal';
import { AddToBeatPackModal } from '@features/sharing/components/AddToBeatPackModal';
import { createPortal } from 'react-dom';
import { supabase } from '@config/supabase';
import { useAuthStore } from '@stores/useAuthStore';
import { getRoleForPlan } from '@config/subscription';

export const CloudExplorer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { openUpload?: boolean; openCreateFolder?: boolean; openFolderId?: string } | null;
  
  // Use persistent store for UI state
  const {
    currentFolder: storeCurrentFolder,
    selectedType,
    selectedStatus,
    selectedBpmRange,
    selectedKey,
    setCurrentFolder: setStoreCurrentFolder,
    setSelectedType,
    setSelectedStatus,
    setSelectedBpmRange,
    setSelectedKey,
  } = useCloudExplorerStore();
  const [showUploader, setShowUploader] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteType, setDeleteType] = useState<'files' | 'folders'>('files');
  const [showAddToBeatPack, setShowAddToBeatPack] = useState(false);
  const [quickEditFile, setQuickEditFile] = useState<AudioFile | null>(null);
  const [hasShownQuickEdit, setHasShownQuickEdit] = useState(false);
  
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userRole = useMemo(
    () => getRoleForPlan(user?.plan ?? UserPlan.FREE),
    [user?.plan]
  );
  const [selectedFile, setSelectedFile] = useState<AudioFile | null>(null);
  const handleCloseAudioPreview = useCallback(() => {
    setSelectedFile(null);
  }, []);
  // Use store's currentFolder but keep local alias for backward compatibility
  const currentFolder = storeCurrentFolder;
  const setCurrentFolder = setStoreCurrentFolder;
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);
  const [isDraggingOverEmpty, setIsDraggingOverEmpty] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [sharedFolderIds, setSharedFolderIds] = useState<Set<string>>(new Set());
  const [uploadWizardState, setUploadWizardState] = useState<{
    files: File[];
    groups?: import('@features/upload/wizard/types').UploadFileGroup[];
    currentStep?: string;
    fileType?: string;
    fileStatus?: string;
    beatstarsSellUrl?: string;
    beatstarsPrice?: string;
    collaborator?: string;
  } | null>(null);
  const { files, folders, fetchFiles, fetchFolders, isLoading, moveFile, moveFolder, updateFile, deleteFile, selectedFiles, selectedFolders, selectMultiple, toggleFolderSelection, clearSelection, clearFolderSelection, deleteFolder, getBlobUrl, searchQuery, preloadUrlsForFiles } = useFileStore();
  const [hasSeenCloudData, setHasSeenCloudData] = useState(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem('cloudExplorer_seen_data') === '1';
  });

  // Fetch all active folder shares
  useEffect(() => {
    const fetchSharedFolders = async () => {
      try {
        const { data: user } = await supabase.auth.getUser();
        if (!user.user) return;

        const { data: shares } = await supabase
          .from('folder_shares')
          .select('folder_id')
          .eq('user_id', user.user.id)
          .eq('is_active', true);

        if (shares) {
          setSharedFolderIds(new Set(shares.map(s => s.folder_id)));
        }
      } catch (error) {
        console.error('Error fetching shared folders:', error);
      }
    };

    if (isAuthenticated) {
      fetchSharedFolders();
    }
  }, [isAuthenticated, folders]); // Re-fetch when folders change

  // CANCEL RESET TIMEOUT: When upload wizard is opened, cancel any pending reset timeout
  useEffect(() => {
    if (showUploader) {
      // Cancel any pending reset timeout when upload wizard is reopened
      const existingTimeout = (window as any).__uploadWizardResetTimeout;
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        (window as any).__uploadWizardResetTimeout = null;
      }
    }
  }, [showUploader]);

  // After Dropbox OAuth redirect: reopen upload modal so Step1 can consume dropbox_resume_cloud_upload
  useEffect(() => {
    if (!user || location.pathname !== ROUTES.CLOUD_EXPLORER) return;
    if (sessionStorage.getItem('dropbox_resume_cloud_upload') === '1') {
      setShowUploader(true);
    }
  }, [user, location.pathname]);

  // Handle Escape key to close upload modal (preserving state)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showUploader) {
        // Get current wizard state before closing
        const stateGetter = (window as any).__uploadWizardState;
        if (stateGetter) {
          const currentState = stateGetter();
          if (currentState && currentState.files && currentState.files.length > 0) {
            setUploadWizardState({
              files: currentState.files,
              groups: currentState.groups,
              currentStep: currentState.currentStep,
              fileType: currentState.fileType,
              fileStatus: currentState.fileStatus,
              beatstarsSellUrl: currentState.beatstarsSellUrl,
              beatstarsPrice: currentState.beatstarsPrice,
              collaborator: currentState.collaborator,
            });
          }
        }
        setShowUploader(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [showUploader]);
  const { success, error: showError } = useToast();
  
  // Check if we're currently searching
  const isSearching = searchQuery && searchQuery.trim() !== '';

  // Drag and Drop functionality
  const handleFileDrop = async (fileId: string, targetFolderId: string | null) => {
    try {
      await moveFile(fileId, targetFolderId);
      success('File moved', 'File has been moved successfully.');
      // No need to refresh - moveFile already updates the store state optimistically
      // React will automatically re-render when the store state changes
    } catch (err) {
      showError('Failed to move file', 'Please try again.');
    }
  };

  const handleFileDropOnEmpty = async (file: AudioFile) => {
    if (!file.groupId) return;
    try {
      await updateFile(file.id, { groupId: undefined });
      success('Track ungrouped', 'The track has been removed from the group.');
      fetchFiles();
    } catch (err) {
      showError('Ungroup failed', 'Please try again.');
    }
  };

  const handleFolderDrop = async (folderId: string, targetFolderId: string | null) => {
    try {
      // Normalize targetFolderId: convert undefined to null
      const normalizedTargetId = targetFolderId === undefined ? null : targetFolderId;
      
      // Prevent moving folder into itself
      if (folderId === normalizedTargetId) {
        console.warn('Cannot move folder into itself');
        showError('Invalid move', 'Cannot move folder into itself.');
        return;
      }
      
      // Prevent moving folder into its own descendants
      const checkDescendants = (folderId: string, targetId: string | null): boolean => {
        if (!targetId) return false;
        const targetFolder = folders.find(f => f.id === targetId);
        if (!targetFolder) return false;
        if (targetFolder.parentId === folderId) return true;
        if (targetFolder.parentId) {
          return checkDescendants(folderId, targetFolder.parentId);
        }
        return false;
      };
      
      if (normalizedTargetId && checkDescendants(folderId, normalizedTargetId)) {
        console.warn('Cannot move folder into its own subfolder');
        showError('Invalid move', 'Cannot move folder into its own subfolder.');
        return;
      }
      
      await moveFolder(folderId, normalizedTargetId);
      success('Folder moved', 'Folder has been moved successfully.');
      
      // No need to refresh - moveFolder already updates the store state optimistically
      // React will automatically re-render when the store state changes
    } catch (err) {
      console.error('❌ Failed to move folder:', err);
      showError('Failed to move folder', err instanceof Error ? err.message : 'Please try again.');
    }
  };

  const {
    isDragging,
    draggedFile,
    handleDragStart,
    handleFolderDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop,
    handleDropOnEmpty,
  } = useDragAndDrop(handleFileDrop, handleFolderDrop, handleFileDropOnEmpty);

  // Only fetch files and folders when user is authenticated and data is not already loaded
  // Use isAuthenticated instead of user?.id to allow parallel loading
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    
    // Only fetch if files/folders are empty (not loaded yet)
    // Check current state directly to avoid dependency issues
    const currentFiles = files.length;
    const currentFolders = folders.length;
    const needsFiles = currentFiles === 0;
    const needsFolders = currentFolders === 0;
    
    if (needsFiles || needsFolders) {
      if (needsFiles) fetchFiles();
      if (needsFolders) fetchFolders();
    }
    // Only run when user auth state changes, not when files/folders change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if ((files.length > 0 || folders.length > 0) && !hasSeenCloudData) {
      setHasSeenCloudData(true);
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('cloudExplorer_seen_data', '1');
      }
    }
  }, [files.length, folders.length, hasSeenCloudData]);

  const showInitialSkeleton = isLoading && files.length === 0 && folders.length === 0 && !hasSeenCloudData;

  // ESC closes upload modal; state is preserved by handleEscape above (no second handler)

  // Check URL params for quick edit (from direct link only - not after upload)
  useEffect(() => {
    if (!user) return;

    // Check URL params for quick edit (from VST direct link)
    const urlParams = new URLSearchParams(window.location.search);
    const fileIdFromUrl = urlParams.get('file');
    const shouldEdit = urlParams.get('edit') === 'tags';

    if (fileIdFromUrl && shouldEdit && !hasShownQuickEdit) {
      // Use files from store directly to avoid dependency issues
      const currentFiles = useFileStore.getState().files;
      const file = currentFiles.find(f => f.id === fileIdFromUrl);
      if (file) {
        setQuickEditFile(file);
        setHasShownQuickEdit(true);
        // Clean URL
        window.history.replaceState({}, '', '/cloud');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, hasShownQuickEdit]);


  // Handle navigation state (open upload/create folder from Quick Actions)
  useEffect(() => {
    if (state?.openUpload) {
      setShowUploader(true);
    }
    if (state?.openCreateFolder) {
      setShowCreateFolder(true);
    }
    if (state?.openFolderId) {
      setCurrentFolder(state.openFolderId);
      const { openFolderId: _omit, ...rest } = state;
      navigate(location.pathname, { replace: true, state: Object.keys(rest).length ? rest : {} });
    }
  }, [state]);

  // Filter files by current folder and type
  // Note: Files without folderId (undefined) should be treated as root folder (null)
  // When searching, ignore folder filter and show all matching files
  // When a type filter is active, show ALL files of that type from all folders
  const currentFolderFiles = isSearching && searchQuery
    ? files // Show all files when searching
    : files.filter((file) => 
        (file.folderId || null) === currentFolder
      );
  
  // WICHTIG: Cloud Explorer zeigt NUR Main Versions + Tagged Versions
  // Producer-Tag PRESETS werden ausgeblendet
  const mainVersionFiles = currentFolderFiles.filter((file) => {
    // Blende Producer-Tag Presets aus
    // 1. Check: Name beginnt mit "ProducerTag_"
    if (file.name.startsWith('ProducerTag_')) return false;
    
    // 2. Check: Hat 'producer-tag' Label in Tags
    const hasProducerTagLabel = file.tags?.some(tag => tag.label === 'producer-tag');
    if (hasProducerTagLabel) return false;
    
    // Zeige Main Versions UND Tagged Versions
    return true;
  });
  
  let filteredFiles = selectedType === 'all'
    ? mainVersionFiles
    : selectedType === 'starred'
    ? files.filter((file) => {
        // Blende Producer-Tag Presets aus
        if (file.name.startsWith('ProducerTag_')) return false;
        const hasProducerTagLabel = file.tags?.some(tag => tag.label === 'producer-tag');
        if (hasProducerTagLabel) return false;
        return file.isStarred;
      })
    : files.filter((file) => {
        // Blende Producer-Tag Presets aus
        if (file.name.startsWith('ProducerTag_')) return false;
        const hasProducerTagLabel = file.tags?.some(tag => tag.label === 'producer-tag');
        if (hasProducerTagLabel) return false;
        return file.type === selectedType;
      });
  
  // Apply status filter
  if (selectedStatus !== 'all') {
    filteredFiles = filteredFiles.filter((file) => file.status === selectedStatus);
  }

  // Apply BPM filter
  if (selectedBpmRange !== 'all') {
    filteredFiles = filteredFiles.filter((file) => {
      if (!file.bpm) return false;
      const bpm = file.bpm;
      
      if (selectedBpmRange === '60-90') return bpm >= 60 && bpm < 90;
      if (selectedBpmRange === '90-120') return bpm >= 90 && bpm < 120;
      if (selectedBpmRange === '120-140') return bpm >= 120 && bpm < 140;
      if (selectedBpmRange === '140-160') return bpm >= 140 && bpm < 160;
      if (selectedBpmRange === '160-180') return bpm >= 160 && bpm < 180;
      if (selectedBpmRange === '180+') return bpm >= 180;
      
      return true;
    });
  }

  // Apply Key filter
  if (selectedKey !== 'all') {
    filteredFiles = filteredFiles.filter((file) => {
      if (!file.key) return false;
      // Match key regardless of minor/major (C, Cm, C#, C#m, etc.)
      return file.key.startsWith(selectedKey);
    });
  }

  // Get folders in current directory
  const currentFolderSubfolders = useMemo(() => {
    return folders.filter((folder) => 
      currentFolder ? folder.parentId === currentFolder : !folder.parentId
    );
  }, [folders, currentFolder]);

  // Group files by similar names (automatic grouping)
  // Important: Re-group when files change to reflect main version updates
  // Also integrate stems folders into groups
  const fileGroups = useMemo(() => {
    const groups = groupFilesByName(filteredFiles);
    
    // Link folders with stems to their matching tracks
    const stemsFoldersMap = linkStemsFoldersToTracks(groups, currentFolderSubfolders);
    
    // Integrate stems folders into groups
    const groupsWithStems = groups.map(group => ({
      ...group,
      stemsFolders: stemsFoldersMap.get(group.id) || []
    }));

    return sortFileGroupsByCreatedAtDesc(groupsWithStems);
  }, [filteredFiles, currentFolderSubfolders]);

  // Stable key from file IDs only — isStarred/metadata changes must not re-trigger preload
  const visiblePreloadIdsKey = useMemo(
    () =>
      fileGroups
        .slice(0, 20)
        .flatMap((g) => [g.mainFile.id, ...g.versions.map((v) => v.id)])
        .slice(0, 25)
        .join(','),
    [
      fileGroups.length,
      fileGroups
        .slice(0, 20)
        .map((g) => `${g.id}:${g.mainFile.id}:${g.versions.map((v) => v.id).join('|')}`)
        .join(';'),
    ]
  );

  // Preload URLs (+ first audio chunk) for visible files so playback starts faster
  useEffect(() => {
    if (!visiblePreloadIdsKey || !preloadUrlsForFiles) return;
    const ids = visiblePreloadIdsKey.split(',').filter(Boolean);
    const storeFiles = useFileStore.getState().files;
    const visibleFiles = ids
      .map((id) => storeFiles.find((f) => f.id === id))
      .filter((f): f is AudioFile => !!f);
    if (visibleFiles.length > 0) {
      preloadUrlsForFiles(visibleFiles);
    }
  }, [visiblePreloadIdsKey, preloadUrlsForFiles]);

  const handleFileClick = (file: AudioFile) => {
    setSelectedFile(file);
  };

  const handleFolderClick = (folder: Folder) => {
    setCurrentFolder(folder.id);
  };

  // Navigate to a specific folder in the breadcrumb
  const handleBreadcrumbClick = (folderId: string | null) => {
    setCurrentFolder(folderId);
  };

  // Build complete folder path from current folder to root
  const breadcrumbPath = useMemo(() => {
    const path: Array<{ id: string | null; name: string }> = [];
    
    if (!currentFolder) {
      // We're at root, only show Root
      return [{ id: null, name: 'Root' }];
    }
    
    // Build path from current folder to root
    let currentFolderId: string | null | undefined = currentFolder;
    while (currentFolderId) {
      const folder = folders.find(f => f.id === currentFolderId);
      if (folder) {
        path.unshift({ id: folder.id, name: folder.name });
        currentFolderId = folder.parentId;
      } else {
        break;
      }
    }
    
    // Always start with Root
    path.unshift({ id: null, name: 'Root' });
    
    return path;
  }, [currentFolder, folders]);

  // Get folder path for a file (legacy function, kept for compatibility)
  const getFolderPath = (folderId: string | undefined): string => {
    if (!folderId) return '/';
    
    const folder = folders.find(f => f.id === folderId);
    if (!folder) return '/';
    
    // Build path recursively
    const buildPath = (folder: Folder): string => {
      if (!folder.parentId) {
        return `/${folder.name}`;
      }
      const parent = folders.find(f => f.id === folder.parentId);
      if (!parent) return `/${folder.name}`;
      return `${buildPath(parent)}/${folder.name}`;
    };
    
    return buildPath(folder);
  };

  // Batch Operations Handlers
  const handleBatchDelete = () => {
    setDeleteType('files');
    setShowDeleteConfirm(true);
  };

  const confirmBatchDelete = async () => {
    try {
      if (deleteType === 'files') {
        const count = selectedFiles.length;
        await Promise.all(selectedFiles.map(id => deleteFile(id)));
        success('Files deleted', `${count} ${count === 1 ? 'file' : 'files'} deleted successfully`);
        clearSelection();
      } else {
        const count = selectedFolders.length;
        await Promise.all(selectedFolders.map(id => deleteFolder(id)));
        success('Folders deleted', `${count} ${count === 1 ? 'folder' : 'folders'} deleted successfully`);
        clearFolderSelection();
      }
      setShowDeleteConfirm(false);
      fetchFiles();
      fetchFolders();
    } catch (err) {
      showError(`Failed to delete ${deleteType}`);
      setShowDeleteConfirm(false);
    }
  };

  const handleBatchChangeStatus = async (status: FileStatus) => {
    try {
      await Promise.all(selectedFiles.map(id => updateFile(id, { status })));
      success('Status updated', `${selectedFiles.length} files updated`);
      fetchFiles();
    } catch (err) {
      showError('Failed to update status');
    }
  };

  const handleBatchAddTags = async (tags: any[]) => {
    try {
      for (const fileId of selectedFiles) {
        const file = files.find(f => f.id === fileId);
        if (file) {
          const existingTags = file.tags || [];
          const newTags = [...existingTags, ...tags.filter(t => !existingTags.find(et => et.id === t.id))];
          await updateFile(fileId, { tags: newTags as any });
        }
      }
      success('Tags added', `Tags added to ${selectedFiles.length} files`);
      fetchFiles();
    } catch (err) {
      showError('Failed to add tags');
    }
  };

  const handleBatchDownload = async () => {
    try {
      let downloadCount = 0;
      
      for (const fileId of selectedFiles) {
        const file = files.find(f => f.id === fileId);
        if (!file) {
          console.warn(`File not found: ${fileId}`);
          continue;
        }

        // Get the download URL
        const downloadUrl = await getBlobUrl(fileId);
        
        if (!downloadUrl) {
          console.warn(`❌ No URL for file: ${file.name}`);
          showError('No URL', `Could not get download URL for ${file.name}`);
          continue;
        }

        try {
          // Try to download via fetch and blob for CORS safety
          const response = await fetch(downloadUrl);
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);

          // Create download link
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = file.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Cleanup blob URL
          setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

          downloadCount++;

          // Small delay between downloads
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (downloadError) {
          console.error(`❌ Failed to download ${file.name}:`, downloadError);
          showError('Download failed', `Could not download ${file.name}`);
        }
      }
      
      if (downloadCount > 0) {
        success('Downloads complete', `${downloadCount} file(s) downloaded successfully`);
      } else {
        showError('No downloads', 'Could not download any files');
      }
    } catch (err) {
      console.error('❌ Batch download error:', err);
      showError('Download failed', 'An error occurred during download');
    }
  };

  const handleBatchLink = async () => {
    if (selectedFiles.length < 2) {
      showError('Link files', 'Select at least 2 files to link');
      return;
    }

    try {
      const groupId = crypto.randomUUID();
      await Promise.all(selectedFiles.map(id => updateFile(id, { groupId })));
      success('Files linked', `${selectedFiles.length} files linked together`);
      clearSelection();
      fetchFiles();
    } catch (err) {
      showError('Failed to link files');
    }
  };

  const handleBatchMove = () => {
    // TODO: Open folder selection modal
    showError('Move files', 'Folder selection coming soon!');
  };

  // Folder Batch Operations Handlers
  const handleBatchFolderDelete = () => {
    setDeleteType('folders');
    setShowDeleteConfirm(true);
  };

  const handleBatchFolderMove = () => {
    // TODO: Open folder selection modal
    showError('Move folders', 'Folder selection coming soon!');
  };

  const handleAddToBeatPack = () => {
    if (selectedFiles.length === 0) {
      showError('No files selected', 'Please select at least one file to create a beat pack.');
      return;
    }

    // Open modal immediately; suspension is checked on submit actions inside the modal.
    setShowAddToBeatPack(true);
  };

  return (
    <div className="@container cloud-explorer-content w-full min-w-0 overflow-x-hidden space-y-3 md:space-y-4 lg:space-y-6">
      {/* Page Header - Mobile First */}
      <div className="flex flex-col gap-3 md:gap-4">
        <div className="flex flex-col @explorer:flex-row @explorer:items-start @explorer:justify-between gap-3 @explorer:gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 md:gap-x-3 gap-y-1.5 mb-1.5">
              {currentFolder ? (
                <button
                  onClick={() => handleBreadcrumbClick(null)}
                  onDragOver={(e) => {
                    handleDragOver(e);
                    setDragOverFolder('root');
                  }}
                  onDragLeave={() => setDragOverFolder(null)}
                  onDrop={(e) => {
                    handleDrop(e, null);
                    setDragOverFolder(null);
                  }}
                  className={cn(
                    "font-dela font-bold text-2xl md:text-3xl lg:text-4xl text-white/50 hover:text-white transition-colors duration-200 uppercase tracking-tight text-left select-none touch-manipulation",
                    dragOverFolder === 'root' && "text-primary"
                  )}
                  title="Go to Root directory"
                >
                  Cloud Explorer
                </button>
              ) : (
                <h1 className="font-dela font-bold text-2xl md:text-3xl lg:text-4xl text-white uppercase tracking-tight">
                  Cloud Explorer
                </h1>
              )}

              {/* Breadcrumbs - Modern Design with Chevrons Inline */}
              {currentFolder && breadcrumbPath.length > 1 && (
                <div className="flex items-center gap-1 text-sm overflow-x-auto scrollbar-hide py-1">
                  {breadcrumbPath.slice(1).map((folder, index) => {
                    const isLast = index === breadcrumbPath.length - 2; // Shifted by 1 due to slice(1)
                    
                    return (
                      <React.Fragment key={folder.id || `folder-${index}`}>
                        <ChevronRight className="w-4 h-4 text-white/20 flex-shrink-0 mx-0.5" />
                        {isLast ? (
                          // Current folder - sleek active state
                          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 border border-primary/20 text-primary rounded-lg flex-shrink-0 shadow-sm shadow-primary/5 transition-all">
                            <FolderIcon className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                            <span className="font-inter font-semibold text-sm truncate max-w-[140px] md:max-w-none">
                              {folder.name}
                            </span>
                          </div>
                        ) : (
                          // Previous subfolders - sleek clickable
                          <button
                            onClick={() => handleBreadcrumbClick(folder.id)}
                            onDragOver={(e) => {
                              handleDragOver(e);
                              setDragOverFolder(folder.id || 'root');
                            }}
                            onDragLeave={() => setDragOverFolder(null)}
                            onDrop={(e) => {
                              handleDrop(e, folder.id);
                              setDragOverFolder(null);
                            }}
                            className={cn(
                              "flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-transparent transition-all duration-200 flex-shrink-0 touch-manipulation active:scale-95 select-none text-white/60 hover:text-white/90 hover:bg-white/5 hover:border-white/10",
                              dragOverFolder === (folder.id || 'root') && "bg-primary/20 text-primary border-primary/40 shadow-lg scale-[1.05]"
                            )}
                          >
                            <FolderIcon className="w-3.5 h-3.5 flex-shrink-0 text-white/50" />
                            <span className="font-inter text-sm truncate max-w-[100px] md:max-w-none">{folder.name}</span>
                          </button>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
            <p className="font-inter text-base md:text-lg lg:text-xl text-white/60">
              {isSearching && searchQuery ? (
                filteredFiles.length === 0 ? (
                  <span className="text-white/60">No results for "{searchQuery}"</span>
                ) : (
                  <span>{filteredFiles.length} {filteredFiles.length === 1 ? 'result' : 'results'} for "{searchQuery}"</span>
                )
              ) : (
                <span>{filteredFiles.length} {filteredFiles.length === 1 ? 'file' : 'files'} found</span>
              )}
            </p>
          </div>

          {/* View Controls - Mobile First with larger touch targets */}
          <div className="flex items-center gap-2 md:gap-3 flex-wrap flex-shrink-0">
            <button
              onClick={() => setShowCreateFolder(true)}
              className="flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-1.5 md:py-2 bg-white/10 hover:bg-white/20 rounded-md transition-all text-sm md:text-base touch-manipulation min-h-[36px] md:min-h-[40px]"
            >
              <FolderPlus className="w-4 h-4 text-white flex-shrink-0" />
              <span className="font-codec font-bold text-sm md:text-base text-white hidden sm:inline">New Folder</span>
            </button>

            <button
              onClick={() => {
                // Cancel any pending reset timeout when upload button is clicked
                const existingTimeout = (window as any).__uploadWizardResetTimeout;
                if (existingTimeout) {
                  clearTimeout(existingTimeout);
                  (window as any).__uploadWizardResetTimeout = null;
                }
                setShowUploader(!showUploader);
              }}
              className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-primary rounded-md shadow-fyle-sm hover:scale-105 active:scale-95 transition-transform touch-manipulation min-h-[36px] md:min-h-[40px]"
            >
              <UploadIcon className="w-4 h-4 text-black flex-shrink-0" />
              <span className="font-codec font-bold text-sm md:text-base text-black">Upload</span>
            </button>

          </div>
        </div>
      </div>

      {/* File Uploader Modal - Mobile First */}
      {showUploader && (
        <>
          {/* Backdrop - Full screen from top - NO GAP */}
          <div 
            className="fixed top-0 left-0 right-0 bottom-0 z-[9999] bg-black/60 backdrop-blur-md animate-fade-in"
            style={{ marginTop: 0, paddingTop: 0 }}
            onClick={async () => {
              // Cleanup uploaded files if workflow was not completed
              const cleanupFn = (window as any).__uploadWizardCleanup;
              if (cleanupFn) {
                await cleanupFn();
              }

              // Close modal on backdrop click, but preserve state
              const stateGetter = (window as any).__uploadWizardState;
              if (stateGetter) {
                const currentState = stateGetter();
                if (currentState && currentState.files && currentState.files.length > 0) {
                  setUploadWizardState({
                    files: currentState.files,
                    groups: currentState.groups,
                    currentStep: currentState.currentStep,
                    fileType: currentState.fileType,
                    fileStatus: currentState.fileStatus,
                    beatstarsSellUrl: currentState.beatstarsSellUrl,
                    beatstarsPrice: currentState.beatstarsPrice,
                    collaborator: currentState.collaborator,
                  });
                }
              }
              setShowUploader(false);
            }}
          />
          {/* Modal Container - pointer-events-auto so clicks on padding don't pass through to backdrop */}
          <div 
            className="fixed top-0 left-0 right-0 bottom-0 sm:top-[71px] md:top-[76px] lg:top-[81px] xl:top-[86px] z-[10000] flex items-center justify-center p-0 sm:p-2 md:p-1.5 lg:p-2 xl:p-2.5 overflow-y-auto sm:overflow-y-visible min-h-0 pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="relative w-full h-full sm:w-full sm:h-auto sm:max-w-lg md:max-w-xl lg:max-w-2xl xl:max-w-3xl 2xl:max-w-4xl sm:max-h-[calc(100vh-90px)] md:max-h-[calc(100vh-100px)] lg:max-h-[calc(100vh-110px)] xl:max-h-[calc(100vh-120px)] overflow-hidden bg-dark-secondary/95 backdrop-blur-lg border-0 sm:border border-white/10 rounded-none sm:rounded-xl md:rounded-2xl lg:rounded-3xl shadow-2xl animate-slide-in-up flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
            {/* Close Button - Removed, use Cancel button in wizard instead */}

            {/* Modal Header - Mobile optimized */}
            <div className="sticky top-0 z-20 px-4 sm:px-5 md:px-3 lg:px-3.5 xl:px-4 py-2.5 sm:py-3 md:py-2 lg:py-2 xl:py-2.5 bg-dark-secondary/95 backdrop-blur-lg border-b border-white/10 flex-shrink-0">
              <h2 className="font-dela text-lg sm:text-xl md:text-base lg:text-lg xl:text-xl 2xl:text-2xl text-white flex items-center gap-2 md:gap-1.5 lg:gap-2">
                <UploadIcon className="w-5 h-5 sm:w-6 sm:h-6 md:w-4 md:h-4 lg:w-4.5 lg:h-4.5 xl:w-5 xl:h-5 text-primary flex-shrink-0" />
                <span>Upload Files</span>
              </h2>
              <p className="mt-1 md:mt-0.5 lg:mt-1 font-inter text-sm sm:text-base md:text-[11px] lg:text-xs xl:text-sm 2xl:text-base text-white/60">
                Add your audio files to the cloud
              </p>
            </div>

            {/* File Uploader Content - Scrollable with mobile-first padding, compact on desktop */}
            <div className="flex-1 overflow-y-auto min-h-0 p-3 sm:p-4 md:p-2 lg:p-2.5 xl:p-3">
              <FileUploader
                initialFiles={
                  droppedFiles.length > 0 
                    ? droppedFiles 
                    : uploadWizardState?.files && uploadWizardState.files.length > 0
                    ? uploadWizardState.files
                    : undefined
                }
                onUploadComplete={async () => {
                  success('Upload successful', 'Your files have been uploaded successfully.');
                  setShowUploader(false);
                  setDroppedFiles([]);
                  setUploadWizardState(null);
                  // Force-Refresh: sonst greift der 5-Min-Client-Cache (early return) oder
                  // veralteter sessionStorage-Clone – Collaborator/BeatStars erscheinen nicht in der Liste.
                  await fetchFiles(true);
                }}
                onCancel={(state) => {
                  // IMMEDIATE: Save state and close (cleanup is handled in Cancel button, don't wait)
                  if (state && state.files && state.files.length > 0) {
                    setUploadWizardState({
                      files: state.files,
                      groups: state.groups,
                      currentStep: state.currentStep,
                      fileType: state.fileType,
                      fileStatus: state.fileStatus,
                      beatstarsSellUrl: state.beatstarsSellUrl,
                      beatstarsPrice: state.beatstarsPrice,
                      collaborator: state.collaborator,
                    });
                  }
                  setShowUploader(false);
                  
                  // BACKGROUND: Cleanup in background (already handled in Cancel button, but extra safety)
                  const cleanupFn = (window as any).__uploadWizardCleanup;
                  if (cleanupFn) {
                    cleanupFn().catch((err: any) => {
                      console.error('Background cleanup failed (non-critical):', err);
                    });
                  }
                  
                  // Expose function to clear state (called by wizard after 10 seconds)
                  (window as any).__uploadWizardClearState = () => {
                    setUploadWizardState(null);
                  };
                }}
                restoredState={uploadWizardState || undefined}
              />
            </div>
            </div>
          </div>
        </>
      )}

      {/* Filters */}
      <div className="mb-6">
        <FileFilters 
          selectedType={selectedType} 
          onTypeChange={setSelectedType}
          selectedStatus={selectedStatus}
          onStatusChange={setSelectedStatus}
          selectedBpmRange={selectedBpmRange}
          onBpmRangeChange={setSelectedBpmRange}
          selectedKey={selectedKey}
          onKeyChange={setSelectedKey}
        />
      </div>

      {/* Split Layout: Folders (Left) | Files (Right) – Skeleton nur wenn wirklich keine Dateien (wie Dashboard) */}
      {showInitialSkeleton ? (
        <div className="py-8 animate-fade-in">
          <div className="flex flex-col gap-4 lg:gap-6">
            {/* Folders Skeleton */}
            <div className="space-y-3">
              <div className="h-8 w-32 bg-white/10 rounded animate-shimmer" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-16 bg-white/5 rounded-lg animate-shimmer" />
                ))}
              </div>
            </div>
            {/* Files Skeleton */}
            <SkeletonTable rows={8} />
          </div>
        </div>
      ) : filteredFiles.length === 0 && currentFolderSubfolders.length === 0 ? (
        <div className="pt-6">
          {/* Show search results empty state if searching, otherwise show normal empty state */}
          {isSearching && searchQuery ? (
            <EmptySearchResults query={searchQuery} />
          ) : (
            <EmptyCloudExplorer
              onUpload={() => {
                setDroppedFiles([]);
                setShowUploader(true);
              }}
              onCreateFolder={!currentFolder ? () => setShowCreateFolder(true) : undefined}
              isDraggingOver={isDraggingOverEmpty}
              currentFolder={currentFolder}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDraggingOverEmpty(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDraggingOverEmpty(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDraggingOverEmpty(false);
                
                // Get dropped files
                const files = Array.from(e.dataTransfer.files);
                if (files.length > 0) {
                  // Pass files to uploader
                  setDroppedFiles(files);
                  setShowUploader(true);
                }
              }}
            />
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4 lg:gap-6">
          {/* Folders Section - Mobile First */}
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-dela text-base md:text-lg text-white flex items-center gap-2">
                <FolderIcon className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                Folders
              </h2>
              <button
                onClick={() => setShowCreateFolder(true)}
                className="p-2 md:p-2.5 bg-primary/20 hover:bg-primary/30 rounded-lg transition-all touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
                title="New Folder"
                aria-label="Create new folder"
              >
                <FolderPlus className="w-4 h-4 md:w-5 md:h-5 text-primary" />
              </button>
            </div>
            
            {currentFolderSubfolders.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 @explorer:grid-cols-3 2xl:grid-cols-4 gap-3 md:gap-4">
                {currentFolderSubfolders.map((folder) => (
                  <div
                    key={folder.id}
                    className="relative group"
                    onDragOver={(e) => {
                      handleDragOver(e);
                      setDragOverFolder(folder.id);
                    }}
                    onDragLeave={() => setDragOverFolder(null)}
                    onDrop={(e) => {
                      handleDrop(e, folder.id);
                      setDragOverFolder(null);
                    }}
                  >
                    <button
                      draggable
                      onDragStart={(e) => {
                        handleFolderDragStart(folder);
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragEnd={handleDragEnd}
                      onClick={(e) => {
                        // Ctrl/Cmd+Click for multi-select
                        if (e.ctrlKey || e.metaKey) {
                          e.stopPropagation();
                          // Clear file selection when selecting folders
                          if (selectedFiles.length > 0) {
                            clearSelection();
                          }
                          toggleFolderSelection(folder.id);
                        } else {
                          handleFolderClick(folder);
                        }
                      }}
                      className={cn(
                        "w-full flex items-center gap-3 p-3 md:p-4 bg-dark-secondary/40 hover:bg-dark-secondary/60 border rounded-lg md:rounded-xl transition-all text-left cursor-pointer touch-manipulation min-h-[64px] md:min-h-[72px] relative overflow-hidden",
                        selectedFolders.includes(folder.id) ? "border-primary bg-primary/20" : "border-white/5",
                        dragOverFolder === folder.id && "border-primary/50 bg-primary/5 scale-[1.02]",
                        sharedFolderIds.has(folder.id) && "pr-5"
                      )}
                    >
                      {/* Green Gradient Indicator on right side if shared */}
                      {sharedFolderIds.has(folder.id) && (
                        <div className="absolute right-0 top-3 bottom-3 md:top-4 md:bottom-4 w-1 bg-gradient-to-b from-primary via-[#30d870] to-[#00ff61] z-10 rounded-l" />
                      )}
                      <div className="p-2 md:p-2.5 bg-primary/20 rounded-lg group-hover:bg-primary/30 transition-colors flex-shrink-0">
                        <FolderIcon className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-inter font-bold text-sm md:text-base text-white truncate">
                          {folder.name}
                        </p>
                        <div className="text-xs md:text-sm text-white/60 mt-0.5">
                          {(() => {
                            // Calculate file count (excluding Producer-Tag Presets)
                            const folderFiles = files.filter(f => {
                              if (f.folderId !== folder.id) return false;
                              // Exclude Producer-Tag Presets
                              if (f.name.startsWith('ProducerTag_')) return false;
                              const hasProducerTagLabel = f.tags?.some(tag => tag.label === 'producer-tag');
                              if (hasProducerTagLabel) return false;
                              return true;
                            });
                            const fileCount = folderFiles.length;
                            
                            // Calculate subfolder count dynamically
                            const subfolderCount = folders.filter(f => f.parentId === folder.id).length;
                            
                            const hasContent = fileCount > 0 || subfolderCount > 0;
                            
                            if (!hasContent) {
                              return <p className="text-white/40">Empty</p>;
                            }
                            
                            return (
                              <div className="flex items-center gap-2 flex-wrap">
                                {fileCount > 0 && (
                                  <span>{fileCount} {fileCount === 1 ? 'File' : 'Files'}</span>
                                )}
                                {subfolderCount > 0 && (
                                  <span className="text-primary/80">{subfolderCount} {subfolderCount === 1 ? 'Folder' : 'Folders'}</span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </button>
                    
                    {/* Folder Actions Menu */}
                    <FolderActions
                      folder={folder}
                      onUpdate={() => {
                        fetchFolders();
                        fetchFiles();
                      }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 md:p-8 bg-dark-secondary/20 rounded-lg md:rounded-xl border border-dashed border-white/10 text-center">
                <FolderIcon className="w-8 h-8 md:w-10 md:h-10 text-white/40 mx-auto mb-2" />
                <p className="text-sm md:text-base text-white/60">No folders yet</p>
              </div>
            )}
          </div>

          {/* Files Section - Always below folders */}
          <div
            className={cn(
              'relative space-y-3 min-w-0 overflow-x-hidden rounded-xl transition-all duration-200 ease-out',
              isDragging && draggedFile?.groupId && 'ring-1 ring-primary/20 ring-dashed bg-white/[0.02]'
            )}
            onDragOver={(e) => {
              if (isDragging && draggedFile) {
                e.preventDefault();
                e.stopPropagation();
                handleDragOver(e);
              }
            }}
            onDrop={(e) => {
              handleDropOnEmpty(e);
            }}
          >
            {isDragging && draggedFile?.groupId && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-10 rounded-xl">
                <span className="px-3 py-1.5 text-primary/90 font-inter text-xs font-medium rounded-full bg-white/5 backdrop-blur-sm border border-white/10 shadow-lg">
                  Drop here to ungroup
                </span>
              </div>
            )}
            <div className="space-y-3 min-w-0 overflow-x-hidden">
            <div className="hidden @explorer:flex items-center justify-between">
              <h2 className="font-dela text-lg text-white">Files</h2>
              {selectedFiles.length === 0 && filteredFiles.length > 0 && (
                <p className="text-xs text-white/40 font-inter">
                  💡 Ctrl+Click to select multiple files
                </p>
              )}
            </div>
            {filteredFiles.length > 0 ? (
                <FileList
                  fileGroups={fileGroups}
                  onFileClick={handleFileClick}
                  onFolderClick={handleFolderClick}
                  onFileUpdate={fetchFiles}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  isDragging={isDragging}
                  getFolderPath={selectedType !== 'all' && selectedType !== 'starred' ? getFolderPath : undefined}
                  selectedFileIds={selectedFiles}
                  onToggleSelect={(fileId) => {
                    if (selectedFolders.length > 0) {
                      clearFolderSelection();
                    }
                    if (selectedFiles.includes(fileId)) {
                      selectMultiple(selectedFiles.filter(id => id !== fileId));
                    } else {
                      selectMultiple([...selectedFiles, fileId]);
                    }
                  }}
                  onViewGroupInVersions={(groupId) => navigate(`${ROUTES.VERSION}?group=${encodeURIComponent(groupId)}`)}
                />
            ) : (showInitialSkeleton) ? (
              <FileListSkeleton count={5} />
            ) : (
              <EmptyFileList onUpload={() => setShowUploader(true)} />
            )}
          </div>
          </div>
        </div>
      )}

      {/* Audio Preview Modal */}
      {selectedFile && (
        <AudioPreview
          file={selectedFile}
          onClose={handleCloseAudioPreview}
        />
      )}

      {/* Create Folder Modal */}
      {showCreateFolder && (
        <CreateFolderModal
          onClose={() => setShowCreateFolder(false)}
          onSuccess={async () => {
            await fetchFolders();
            await fetchFiles();
            setShowCreateFolder(false);
          }}
          parentId={currentFolder ? currentFolder : undefined}
        />
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <UpgradeModal
          currentRole={userRole}
          reason="storage_limit"
          onClose={() => setShowUpgradeModal(false)}
          onUpgrade={(_role) => {
            // TODO: Implement upgrade logic
            setShowUpgradeModal(false);
          }}
          onPurchaseAddon={(_addonId) => {
            // TODO: Implement addon purchase logic
            setShowUpgradeModal(false);
          }}
        />
      )}

      {/* Batch Actions Bar - Files */}
      {selectedFiles.length > 0 && selectedFolders.length === 0 && (
        <BatchActions
          selectedCount={selectedFiles.length}
          onClear={clearSelection}
          onDelete={handleBatchDelete}
          onMove={handleBatchMove}
          onAddTags={handleBatchAddTags}
          onChangeStatus={handleBatchChangeStatus}
          onDownload={handleBatchDownload}
          onLink={handleBatchLink}
          onCreateBeatPack={handleAddToBeatPack}
        />
      )}

      {/* Batch Actions Bar - Folders */}
      {selectedFolders.length > 0 && (
        <BatchActions
          selectedCount={selectedFolders.length}
          type="folders"
          onClear={clearFolderSelection}
          onDelete={handleBatchFolderDelete}
          onMove={handleBatchFolderMove}
          onAddTags={() => {}} // Not applicable for folders
          onChangeStatus={() => {}} // Not applicable for folders
          onDownload={() => {}} // Not applicable for folders
          onLink={() => {}} // Not applicable for folders
        />
      )}

      {/* Delete Confirmation Modal - Mobile First */}
      {showDeleteConfirm &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-dark-secondary/95 backdrop-blur-md border border-white/10 rounded-xl md:rounded-2xl shadow-2xl p-5 md:p-6 max-w-md w-full animate-fade-in">
              <h3 className="font-dela text-lg md:text-xl text-white mb-2">
                Delete {deleteType === 'files' ? 'Files' : 'Folders'}?
              </h3>
              <p className="font-inter text-sm md:text-base text-white/70 mb-6">
                Are you sure you want to delete <span className="text-primary font-bold">
                  {deleteType === 'files' 
                    ? `${selectedFiles.length} ${selectedFiles.length === 1 ? 'file' : 'files'}`
                    : `${selectedFolders.length} ${selectedFolders.length === 1 ? 'folder' : 'folders'}`
                  }
                </span>? This action cannot be undone.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-3 md:py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all font-inter font-bold touch-manipulation min-h-[44px] order-2 sm:order-1"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmBatchDelete}
                  className="px-4 py-3 md:py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg transition-all font-inter font-bold shadow-lg shadow-red-500/20 touch-manipulation min-h-[44px] order-1 sm:order-2"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Quick Edit Modal (opens after VST upload) */}
      {quickEditFile && (
        <QuickEditModal
          file={quickEditFile}
          onClose={() => {
            setQuickEditFile(null);
            setHasShownQuickEdit(false);
          }}
          onSave={() => {
            fetchFiles();
          }}
        />
      )}

      {/* Add To Beat Pack Modal */}
      {showAddToBeatPack && (
        <AddToBeatPackModal
          onClose={() => {
            setShowAddToBeatPack(false);
            // Clear selection after closing - user has completed the action
            clearSelection();
          }}
          fileIds={selectedFiles}
        />
      )}
    </div>
  );
};

